import { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, InputAdornment, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useNavigate } from 'react-router-dom';
import { callConfig } from '../../configs/call';
import { useUser } from '../../hooks/useUser';
import { getCallableUsers, searchCallableUsers } from '../../services/call';
import DashboardTabs from '../dashboard/DashboardTabs';
import { dashboardTabs } from '../dashboard/dashboardMockData';

const quickActionButtonSx = {
    width: 40,
    height: 40,
    minWidth: 40,
    minHeight: 40,
    borderRadius: '50%'
};

const PAGE_SIZE = 4;

const formatClinicianType = (value) => {
    if (!value || typeof value !== 'string' || value === 'UNKNOWN') {
        return '--';
    }

    const map = {
        NURSING: 'Enfermagem',
        CARDIOLOGY: 'Cardiologia',
        GERIATRICS: 'Geriatria',
        NUTRITION: 'Nutrição',
        NEUROLOGY: 'Neurologia',
        PSYCHIATRY: 'Psiquiatria',
        PSYCHOLOGY: 'Psicologia',
        PHYSICAL_THERAPY: 'Fisioterapia',
        OCCUPATIONAL_THERAPY: 'Terapia Ocupacional',
        SOCIAL_ASSISTANT: 'Assistente Social',
        GENERAL_PRACTICE: 'Medicina Geral'
    };

    return map[value] || value;
};

const formatLastAccess = (value) => {
    if (!value || typeof value !== 'string') {
        return '--';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString('pt-PT');
};

const isCoordinatorRole = (role) => role === 'ACCOUNT_ADMIN' || role === 'BCK_ADMIN';

const clinicianTypeIconMap = {
    NURSING: '/assets/logos-svg/nursing.svg',
    CARDIOLOGY: '/assets/logos-svg/cardiology.svg',
    GERIATRICS: '/assets/logos-svg/geriatrics.svg',
    NUTRITION: '/assets/logos-svg/nutrition.svg',
    NEUROLOGY: '/assets/logos-svg/neurology.svg',
    PSYCHIATRY: '/assets/logos-svg/psychiatry.svg',
    PSYCHOLOGY: '/assets/logos-svg/psychology.svg',
    PHYSICAL_THERAPY: '/assets/logos-svg/physicalTherapy.svg',
    OCCUPATIONAL_THERAPY: '/assets/logos-svg/occupational.svg',
    SOCIAL_ASSISTANT: '/assets/logos-svg/socialAssistant.svg',
    GENERAL_PRACTICE: '/assets/logos-svg/generalPractice.svg'
};

const TeamDirectoryPage = ({
    title,
    activeTab,
    roleFilters = [],
    showAgendaShortcut = false
}) => {
    const navigate = useNavigate();
    const { user } = useUser();

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageTokens, setPageTokens] = useState({ 0: null });
    const [hasNextPage, setHasNextPage] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const currentPageToken = pageTokens[pageIndex] || null;

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery.trim());
        }, 350);

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    useEffect(() => {
        let cancelled = false;
        let intervalId = null;

        const loadUsers = async () => {
            setLoading(true);
            setError(null);

            try {
                const currentUserId =
                    callConfig.mode === 'local-dev' ? callConfig.devUserId : user.selectedRole;
                const response = debouncedSearchQuery
                    ? await searchCallableUsers({
                          query: debouncedSearchQuery,
                          currentUserId,
                          userRoleUuid: user.selectedRole,
                          roleFilters,
                          paginationToken: currentPageToken,
                          paginationLimit: PAGE_SIZE,
                          includeCurrentUser: true
                      })
                    : await getCallableUsers({
                          currentUserId,
                          userRoleUuid: user.selectedRole,
                          roleFilters,
                          paginationToken: currentPageToken,
                          paginationLimit: PAGE_SIZE,
                          includeCurrentUser: true
                      });

                if (!cancelled) {
                    setUsers(response.users || []);
                    setTotalCount(Number(response?.totalCount || 0));
                    const nextToken = response?.nextToken || null;
                    const nextPageStart = (pageIndex + 1) * PAGE_SIZE;
                    setHasNextPage(Boolean(nextToken) && nextPageStart < Number(response?.totalCount || 0));
                    if (nextToken) {
                        setPageTokens((prev) => ({
                            ...prev,
                            [pageIndex + 1]: nextToken
                        }));
                    }
                }
            } catch (nextError) {
                if (!cancelled) {
                    const message =
                        nextError instanceof Error ? nextError.message : String(nextError);
                    setError(message);
                    setHasNextPage(false);
                    setTotalCount(0);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        void loadUsers();
        intervalId = setInterval(() => {
            void loadUsers();
        }, 30000);

        return () => {
            cancelled = true;
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [currentPageToken, debouncedSearchQuery, pageIndex, roleFilters, user.selectedRole]);

    const rows = useMemo(
        () =>
            users.map((targetUser) => ({
                ...targetUser,
                roleLabel: formatClinicianType(targetUser.clinicianType),
                lastAccessLabel: formatLastAccess(targetUser.lastCallPing),
                isCoordinator: isCoordinatorRole(targetUser.role),
                isSelf: targetUser.userId === user.selectedRole
            })),
        [user.selectedRole, users]
    );

    const totalPages = useMemo(() => {
        if (totalCount <= 0) {
            return 1;
        }

        return Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    }, [totalCount]);

    useEffect(() => {
        setPageIndex(0);
        setPageTokens({ 0: null });
        setHasNextPage(false);
    }, [debouncedSearchQuery, roleFilters]);

    const handleTabClick = (tab) => {
        if (tab === 'Início') {
            navigate('/');
            return;
        }

        if (tab === 'Agendamentos') {
            navigate('/appointments');
            return;
        }

        if (tab === 'Equipa Técnica') {
            navigate('/doctors');
            return;
        }

        if (tab === 'Cuidadores e Pacientes') {
            navigate('/caregivers');
            return;
        }

        if (tab === 'Documentos') {
            navigate('/documents');
        }
    };

    const isCaregiverRole = (role) => role === 'PATIENT_CARE_GIVER';
    const isCaregiverDirectory = roleFilters.length === 1 && roleFilters[0] === 'PATIENT_CARE_GIVER';
    const showCargoColumn = !(
        roleFilters.length === 1 && roleFilters[0] === 'PATIENT_CARE_GIVER'
    );
    const gridTemplateColumns = showCargoColumn ? '2fr 1.2fr 1.8fr 180px' : '2fr 1.8fr 180px';

    const getRowIconSrc = (row) => {
        if (isCaregiverDirectory) {
            return '/assets/logos-svg/caregiverBlack.svg';
        }

        return clinicianTypeIconMap[row.clinicianType] || '/assets/logos-svg/person.svg';
    };

    return (
        <Box
            sx={{
                px: { xs: 1.5, md: 2 },
                pb: { xs: 1.5, md: 2 },
                pt: 0,
                bgcolor: '#FFFFFF'
            }}
        >
            <Box sx={{ mt: 0 }}>
                <DashboardTabs
                    tabs={dashboardTabs}
                    onTabClick={handleTabClick}
                    activeTab={activeTab}
                />
            </Box>

            <Paper
                variant="elevation=0"
                sx={{
                    p: { xs: 1.8, md: 2.2 },
                    bgcolor: '#FFFFFF',
                    borderRadius: '20px',
                    boxShadow: 'none'
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                    <Typography variant="h4" fontWeight={700} color="#3D3D3D">
                        {title}
                    </Typography>
                </Stack>

                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        placeholder="Pesquisar por nome ou e-mail"
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        sx={{
                            maxWidth: 420,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '14px',
                                bgcolor: '#FFFFFF'
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRoundedIcon sx={{ color: '#7A7A7A' }} />
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>

                <Paper
                    sx={{
                        px: 2.2,
                        py: 1.1,
                        display: 'grid',
                        gridTemplateColumns,
                        gap: 1.2,
                        bgcolor: '#3D3D3D',
                        color: '#fff',
                        borderRadius: '10px',
                        boxShadow: 'none'
                    }}
                >
                    <Typography variant="caption" fontWeight={600}>Equipa</Typography>
                    {showCargoColumn ? <Typography variant="caption" fontWeight={600}>Cargo</Typography> : null}
                    <Typography variant="caption" fontWeight={600}>Último Acesso</Typography>
                    <Typography variant="caption" fontWeight={600} textAlign="right">
                        Ações Rápidas
                    </Typography>
                </Paper>

                <Stack spacing={1} mt={1}>
                    {loading ? (
                        <Paper sx={{ px: 2.2, py: 1.5, bgcolor: '#ECECEC', borderRadius: '12px', boxShadow: 'none' }}>
                            <Typography>Loading team...</Typography>
                        </Paper>
                    ) : null}

                    {!loading && error ? (
                        <Paper sx={{ px: 2.2, py: 1.5, bgcolor: '#ECECEC', borderRadius: '12px', boxShadow: 'none' }}>
                            <Typography color="error">Failed to load users: {error}</Typography>
                        </Paper>
                    ) : null}

                    {!loading && !error && rows.length === 0 ? (
                        <Paper sx={{ px: 2.2, py: 1.5, bgcolor: '#ECECEC', borderRadius: '12px', boxShadow: 'none' }}>
                            <Typography>
                                {debouncedSearchQuery
                                    ? 'Sem resultados para a pesquisa.'
                                    : 'No team users available.'}
                            </Typography>
                        </Paper>
                    ) : null}

                    {!loading && !error
                        ? rows.map((row) => (
                              <Paper
                                  key={row.userId}
                                  sx={{
                                      px: 2.2,
                                      py: 1.35,
                                      display: 'grid',
                                      gridTemplateColumns,
                                      gap: 1.2,
                                      alignItems: 'center',
                                      bgcolor: '#ECECEC',
                                      borderRadius: '12px',
                                      boxShadow: 'none'
                                  }}
                              >
                                  <Stack direction="row" spacing={1} alignItems="center">
                                       <Box
                                           sx={{
                                               width: 10,
                                              height: 10,
                                              borderRadius: '50%',
                                               bgcolor: row.isOnline ? '#26a65b' : '#d64541'
                                           }}
                                       />
                                       {!showCargoColumn ? (
                                           <Box
                                               component="img"
                                               src={getRowIconSrc(row)}
                                               alt={row.roleLabel || 'Utilizador'}
                                               sx={{ width: 20, height: 20, display: 'block', flexShrink: 0 }}
                                           />
                                       ) : null}
                                       <Typography variant="body2" fontWeight={600} color="#3D3D3D">
                                           {row.displayName || row.userId}
                                       </Typography>
                                      {row.isCoordinator ? (
                                          <Box
                                              sx={{
                                                  px: 1,
                                                  py: 0.3,
                                                  borderRadius: '999px',
                                                  bgcolor: 'rgba(214, 55, 49, 0.10)',
                                                  color: '#D63731',
                                                  fontSize: '0.72rem',
                                                  fontWeight: 700,
                                                  lineHeight: 1.2
                                              }}
                                          >
                                              Coordenador
                                          </Box>
                                      ) : null}
                                      {row.isSelf ? (
                                          <Box
                                              sx={{
                                                  px: 1,
                                                  py: 0.3,
                                                  borderRadius: '999px',
                                                  bgcolor: 'rgba(61, 61, 61, 0.08)',
                                                  color: '#3D3D3D',
                                                  fontSize: '0.72rem',
                                                  fontWeight: 700,
                                                  lineHeight: 1.2
                                              }}
                                          >
                                              Tu
                                          </Box>
                                      ) : null}
                                   </Stack>
                                   {showCargoColumn ? (
                                       <Stack direction="row" spacing={0.9} alignItems="center">
                                           <Box
                                               component="img"
                                               src={getRowIconSrc(row)}
                                               alt={row.roleLabel || 'Utilizador'}
                                               sx={{ width: 20, height: 20, display: 'block', flexShrink: 0 }}
                                           />
                                           <Typography variant="body2" fontWeight={600} color="text.secondary">
                                               {row.roleLabel}
                                           </Typography>
                                       </Stack>
                                   ) : null}
                                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                                      {row.lastAccessLabel}
                                  </Typography>
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        {showAgendaShortcut ? (
                                            <Tooltip title="Abrir agenda">
                                                <IconButton
                                                    size="small"
                                                    sx={{
                                                        ...quickActionButtonSx,
                                                        bgcolor: '#FFFFFF',
                                                        color: '#3D3D3D',
                                                        border: '1.5px solid #3D3D3D',
                                                        '&:hover': {
                                                            bgcolor: '#F7F7F7',
                                                            borderColor: '#3D3D3D'
                                                        }
                                                    }}
                                                    onClick={() =>
                                                        navigate('/appointments', {
                                                            state: {
                                                                agendaRoleUuid: row.userId,
                                                                agendaUser: row
                                                            }
                                                        })
                                                    }
                                                >
                                                    <Box
                                                        component="img"
                                                        src="/assets/logos-svg/agendaBlack.svg"
                                                        alt="Agenda"
                                                        sx={{ width: 18, height: 18, display: 'block' }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        ) : null}
                                        <Tooltip title={isCaregiverRole(row.role) ? 'Abrir ficha do cuidador' : 'Abrir ficha do utilizador'}>
                                            <IconButton
                                                size="small"
                                                sx={{
                                                    ...quickActionButtonSx,
                                                    bgcolor: '#3D3D3D',
                                                    color: '#ffffff',
                                                    '&:hover': {
                                                        bgcolor: '#575757'
                                                    }
                                                }}
                                                onClick={() => {
                                                    if (isCaregiverRole(row.role)) {
                                                        navigate(`/caregivers/${row.userId}`, {
                                                            state: { user: row }
                                                        });
                                                        return;
                                                    }

                                                    navigate(`/users/${row.userId}/edit`, {
                                                        state: { user: row }
                                                    });
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src="/assets/logos-svg/openWhite.svg"
                                                    alt="Abrir"
                                                    sx={{ width: 18, height: 18, display: 'block' }}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                        {!row.isSelf ? (
                                            <Tooltip title={row.isOnline ? 'Iniciar videochamada' : 'Começar Videochamada'}>
                                                <IconButton
                                                    size="small"
                                                    sx={{
                                                        ...quickActionButtonSx,
                                                        bgcolor: row.isOnline ? '#E91C24' : '#dd8a8d',
                                                        color: '#fff',
                                                        border: row.isOnline ? 'none' : '1.5px solid #D9D9D9',
                                                        '&:hover': {
                                                            bgcolor: row.isOnline ? '#ddb4b5' : '#ddb4b5'
                                                        },
                                                        '&.Mui-disabled': {
                                                            bgcolor: '#FFFFFF',
                                                            color: '#3D3D3D',
                                                            opacity: 1,
                                                            border: '1.5px solid #D9D9D9'
                                                        }
                                                    }}
                                                    onClick={() =>
                                                        navigate(`/call/${row.userId}`, {
                                                            state: {
                                                                autoDial: true,
                                                                user: row,
                                                                wakeIfOffline: true
                                                            }
                                                        })
                                                    }
                                                >
                                                    <Box
                                                        component="img"
                                                        src="/assets/logos-svg/call.svg"
                                                        alt="Videochamada"
                                                        sx={{ width: 18, height: 18, display: 'block' }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        ) : null}
                                  </Stack>
                              </Paper>
                          ))
                        : null}
                </Stack>

                {!loading && !error && rows.length > 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.2 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <Tooltip title="Página anterior">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => setPageIndex((prev) => Math.max(prev - 1, 0))}
                                        disabled={pageIndex === 0}
                                        sx={{ color: pageIndex === 0 ? '#b8b8b8' : '#5a5a5a' }}
                                    >
                                        <ChevronLeftRoundedIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Typography variant="body2" color="text.secondary">
                                {`${pageIndex + 1} de ${totalPages}`}
                            </Typography>
                            <Tooltip title="Página seguinte">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            if (hasNextPage) {
                                                setPageIndex((prev) => prev + 1);
                                            }
                                        }}
                                        disabled={!hasNextPage}
                                        sx={{ color: hasNextPage ? '#5a5a5a' : '#b8b8b8' }}
                                    >
                                        <ChevronRightRoundedIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    </Box>
                ) : null}
            </Paper>
        </Box>
    );
};

export default TeamDirectoryPage;

